import { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  Wrap,
  WrapItem,
  Input,
  Spinner,
} from '@chakra-ui/react'
import { TicketService } from '@/lib/services/ticket'
import { taskService } from '@/lib/services/taskService'
import type { AgentOption } from '@/types/ticket'
import { useToastNotification } from '@/lib/utils/useToastNotification'

export type TaskWatcherEntry = {
  user_id: number
  name: string
}

type Props = {
  taskId: number
  watchers: TaskWatcherEntry[]
  onChange: (watchers: TaskWatcherEntry[]) => void
  isDisabled?: boolean
}

export default function TaskWatchersControl({ taskId, watchers, onChange, isDisabled = false }: Props) {
  const { showError, showSuccess } = useToastNotification()
  const [search, setSearch] = useState('')
  const [results, setResults] = useState<AgentOption[]>([])
  const [loading, setLoading] = useState(false)
  const [busyUserId, setBusyUserId] = useState<number | null>(null)

  useEffect(() => {
    setSearch('')
    setResults([])
  }, [taskId])

  const agentLabel = (agent: AgentOption) =>
    [agent.first_name, agent.last_name].filter(Boolean).join(' ') ||
    agent.user_login ||
    `#${agent.id}`

  const searchUsers = useCallback(async (term: string) => {
    setSearch(term)
    if (term.length < 2) {
      setResults([])
      return
    }
    setLoading(true)
    try {
      const res = await TicketService.searchHelpdeskAgentOptions(term)
      setResults(res.success && res.data ? res.data : [])
    } finally {
      setLoading(false)
    }
  }, [])

  const handleAdd = async (agent: AgentOption) => {
    if (watchers.some((w) => w.user_id === agent.id)) {
      setSearch('')
      setResults([])
      return
    }
    setBusyUserId(agent.id)
    try {
      await taskService.addWatcher(taskId, agent.id)
      onChange([...watchers, { user_id: agent.id, name: agentLabel(agent) }])
      showSuccess('Watcher added')
      setSearch('')
      setResults([])
    } catch (err) {
      showError(err as Error)
    } finally {
      setBusyUserId(null)
    }
  }

  const handleRemove = async (userId: number) => {
    setBusyUserId(userId)
    try {
      await taskService.removeWatcher(taskId, userId)
      onChange(watchers.filter((w) => w.user_id !== userId))
      showSuccess('Watcher removed')
    } catch (err) {
      showError(err as Error)
    } finally {
      setBusyUserId(null)
    }
  }

  return (
    <FormControl>
      <FormLabel fontSize="xs" mb={1}>
        Watchers
      </FormLabel>
      <Wrap spacing={2} mb={watchers.length ? 2 : 0}>
        {watchers.length === 0 ? (
          <Text fontSize="xs" color="gray.500">
            No watchers yet
          </Text>
        ) : (
          watchers.map((watcher) => (
            <WrapItem key={watcher.user_id}>
              <Tag size="md" borderRadius="full" colorScheme="blue" variant="subtle">
                <TagLabel>{watcher.name}</TagLabel>
                {!isDisabled ? (
                  <TagCloseButton
                    isDisabled={busyUserId === watcher.user_id}
                    onClick={() => void handleRemove(watcher.user_id)}
                  />
                ) : null}
              </Tag>
            </WrapItem>
          ))
        )}
      </Wrap>
      {!isDisabled ? (
        <>
          <Input
            size="sm"
            value={search}
            onChange={(e) => void searchUsers(e.target.value)}
            placeholder="Add watcher (min 2 chars)"
            isDisabled={busyUserId != null}
          />
          {loading ? <Spinner size="sm" mt={2} /> : null}
          {results.length > 0 ? (
            <Box mt={2} borderWidth="1px" borderRadius="md" maxH="160px" overflowY="auto">
              {results.map((agent) => (
                <Button
                  key={agent.id}
                  variant="ghost"
                  size="sm"
                  w="100%"
                  justifyContent="flex-start"
                  isDisabled={busyUserId != null || watchers.some((w) => w.user_id === agent.id)}
                  onClick={() => void handleAdd(agent)}
                >
                  {agentLabel(agent)}
                </Button>
              ))}
            </Box>
          ) : null}
        </>
      ) : null}
    </FormControl>
  )
}
